(() => {
  const nativePlay = HTMLMediaElement.prototype.play;

  const probe = document.createElement('video');
  const supportsHls = !!probe.canPlayType('application/vnd.apple.mpegurl');

  // Swiper loop mode clones slides with cloneNode(true): dataset flags would be copied onto
  // videos that were never registered here, and the play() override would not. Element-keyed
  // sets keep the state off the DOM so clones are treated as new videos.
  const registered = new WeakSet();
  const prepared = new WeakSet();
  const sourced = new WeakSet();

  const resolveSrc = (video) => {
    const hls = video.dataset.srcHls;
    return (supportsHls && hls) || video.dataset.src || hls;
  };

  const attachSource = (video) => {
    if (sourced.has(video)) return;
    const src = resolveSrc(video);
    if (!src) return;
    sourced.add(video);

    // canPlayType reports "maybe" for HLS in browsers that cannot actually decode it,
    // so the mp4 fallback is wired to the error event rather than to feature detection.
    if (src === video.dataset.srcHls && video.dataset.src) {
      video.addEventListener(
        'error',
        () => {
          video.src = video.dataset.src;
          video.load();
          if (video.dataset.autoplay === 'true') video.play();
        },
        { once: true }
      );
    }

    if (video.getAttribute('src') === src) return;

    video.src = src;
    video.load();
  };

  const prepare = (video) => {
    if (prepared.has(video)) return;
    prepared.add(video);

    // play() rejects with AbortError whenever a pause() lands before the promise settles.
    // Theme modules call play() without a handler, so the rejection surfaces as a console error.
    video.play = function play() {
      attachSource(this);
      return nativePlay.call(this).catch((error) => {
        if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
          console.warn('lazy-video:', error);
        }
      });
    };
  };

  const activate = (video) => {
    attachSource(video);
    if (video.dataset.autoplay === 'true') video.play();
  };

  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, instance) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              instance.unobserve(entry.target);
              activate(entry.target);
            });
          },
          { rootMargin: '200px' }
        )
      : null;

  const registerVideo = (video) => {
    if (registered.has(video)) return;
    registered.add(video);
    prepare(video);
    if (observer) {
      observer.observe(video);
    } else {
      activate(video);
    }
  };

  const register = (root) => {
    const scope = root || document;
    if (scope.nodeType === 1 && scope.matches('video[data-lazy-video]')) {
      registerVideo(scope);
      return;
    }
    if (!scope.querySelectorAll) return;
    scope.querySelectorAll('video[data-lazy-video]').forEach(registerVideo);
  };

  const queue = new Set();
  let frame = null;

  const flush = () => {
    frame = null;
    queue.forEach(register);
    queue.clear();
  };

  const domObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) queue.add(node);
      });
    });
    if (queue.size && frame === null) frame = requestAnimationFrame(flush);
  });

  register();
  domObserver.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (event) => register(event.target));
})();
